import type { ClientDTO, ClientTimeline } from '@brainkey/shared';
import { useQueries } from '@tanstack/react-query';

import { clientsApi } from '../api/endpoints';
import { queryKeys } from '../queryClient';

import { useClientsList } from './useClients';

/** Bounded window for the cross-client aggregation (see DEC-0023). */
export const ACTIVITY_AGGREGATE_WINDOW = 30;

/** Same "anything still in flight" condition useClientTimeline uses,
 * applied per-client here so the Dashboard's aggregated activity feed also
 * freshens without a manual reload (CP-P2-T03) — conditional, not a flat
 * interval, so a coach idling on the Dashboard doesn't leave up to 30
 * parallel requests polling forever once everything's settled. */
function hasInFlightActivity(timeline: ClientTimeline | undefined): boolean {
  if (!timeline) return false;
  const hasPending = timeline.questionnaires.some((q) => q.dispatchStatus !== 'RESPONDED');
  const hasInProgressReport = timeline.reports.some(
    (r) => r.status === 'PENDING' || r.status === 'GENERATING',
  );
  return hasPending || hasInProgressReport;
}

export interface ActivityAggregate {
  /** The clients the aggregate was computed over (most recent, bounded). */
  clients: ClientDTO[];
  /** Only populated for clients whose timeline fetch has succeeded so far. */
  timelines: Map<string, ClientTimeline>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fans out a bounded, parallel `GET /clients/:id/timeline` fetch across the
 * most recent clients and aggregates the results in the browser. There is no
 * cross-client reports/questionnaires listing endpoint on the backend (see
 * DEC-0023), so this is a deliberate, explicitly scale-limited stand-in —
 * shared by the Dashboard and the Questionnaire Manager so the N+1 fetch
 * logic isn't duplicated. Reuses `queryKeys.clients.timeline(id)`, the same
 * cache key `useClientTimeline` (the client-detail page) populates, so
 * navigating between an aggregate view and a client's detail page is often a
 * cache hit rather than a re-fetch.
 */
export function useActivityAggregate(): ActivityAggregate {
  const clientsQuery = useClientsList({ page: 1, pageSize: ACTIVITY_AGGREGATE_WINDOW });
  const clients = clientsQuery.data?.items ?? [];

  const timelineQueries = useQueries({
    queries: clients.map((client) => ({
      queryKey: queryKeys.clients.timeline(client.id),
      queryFn: ({ signal }: { signal: AbortSignal }) => clientsApi.timeline(client.id, signal),
      staleTime: 30_000,
      refetchInterval: (query: { state: { data: ClientTimeline | undefined } }) =>
        hasInFlightActivity(query.state.data) ? 30_000 : false,
    })),
  });

  const timelines = new Map<string, ClientTimeline>();
  timelineQueries.forEach((query, index) => {
    const client = clients[index];
    if (client && query.data) timelines.set(client.id, query.data);
  });

  return {
    clients,
    timelines,
    isLoading:
      clientsQuery.isLoading || (clients.length > 0 && timelineQueries.some((q) => q.isLoading)),
    isError: clientsQuery.isError,
  };
}
